import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  sanitizeRichTextHtml,
  richTextLength,
  richTextToPlainText,
} from "../../utils/richText";
import {
  EditorWrapper,
  Toolbar,
  ToolbarButton,
  EditorArea,
  EditorFooter,
  EditorHint,
  CharCounter,
} from "./styles";

const TOOLS = [
  { command: "bold", label: "B", title: "Negrito (Ctrl+B)" },
  { command: "italic", label: "I", title: "Itálico (Ctrl+I)" },
  { command: "underline", label: "U", title: "Sublinhado (Ctrl+U)" },
  { command: "insertUnorderedList", label: "• Lista", title: "Lista com marcadores" },
  { command: "insertOrderedList", label: "1. Lista", title: "Lista numerada" },
];

export default function RichTextEditor({
  id,
  value = "",
  onChange,
  maxLength,
  placeholder = "",
  disabled = false,
  hint,
  style,
}) {
  const editorRef = useRef(null);
  const lastValidHtml = useRef(value);
  const [activeCommands, setActiveCommands] = useState({});

  const plainLength = richTextLength(value);
  const limitReached = Boolean(maxLength) && plainLength >= maxLength;

  // Sincroniza conteúdo externo (ex.: preenchimento a partir de uma solicitação)
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (editor.innerHTML === value) return;
    if (document.activeElement === editor) return;

    editor.innerHTML = value || "";
    lastValidHtml.current = value || "";
  }, [value]);

  const refreshActiveCommands = useCallback(() => {
    const next = {};
    TOOLS.forEach(({ command }) => {
      try {
        next[command] = document.queryCommandState(command);
      } catch {
        next[command] = false;
      }
    });
    setActiveCommands(next);
  }, []);

  const emitChange = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const sanitized = sanitizeRichTextHtml(editor.innerHTML);

    if (maxLength && richTextLength(sanitized) > maxLength) {
      editor.innerHTML = lastValidHtml.current;
      return;
    }

    lastValidHtml.current = sanitized;
    onChange?.(sanitized);
  }, [maxLength, onChange]);

  const runCommand = (command) => {
    const editor = editorRef.current;
    if (!editor || disabled) return;

    editor.focus();
    document.execCommand(command, false, null);
    refreshActiveCommands();
    emitChange();
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const text = event.clipboardData?.getData("text/plain") || "";
    if (!text) return;

    if (maxLength) {
      const remaining = maxLength - plainLength;
      if (remaining <= 0) return;
      document.execCommand("insertText", false, text.slice(0, remaining));
    } else {
      document.execCommand("insertText", false, text);
    }

    emitChange();
  };

  const handleBeforeInput = (event) => {
    if (!maxLength || !limitReached) return;

    const selection = window.getSelection();
    const isReplacing = selection && !selection.isCollapsed;
    const isDeleting = event.inputType?.startsWith("delete");

    if (!isReplacing && !isDeleting) event.preventDefault();
  };

  return (
    <EditorWrapper style={style}>
      <Toolbar>
        {TOOLS.map((tool) => (
          <ToolbarButton
            key={tool.command}
            type="button"
            title={tool.title}
            disabled={disabled}
            $active={Boolean(activeCommands[tool.command])}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => runCommand(tool.command)}
          >
            {tool.label}
          </ToolbarButton>
        ))}
      </Toolbar>

      <EditorArea
        id={id}
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder || "Editor de texto"}
        data-placeholder={placeholder}
        onInput={emitChange}
        onBeforeInput={handleBeforeInput}
        onPaste={handlePaste}
        onKeyUp={refreshActiveCommands}
        onMouseUp={refreshActiveCommands}
        onBlur={emitChange}
      />

      <EditorFooter>
        <EditorHint>{hint || "Use a barra acima para formatar o texto."}</EditorHint>
        {maxLength ? (
          <CharCounter $limitReached={limitReached}>
            {plainLength}/{maxLength}
          </CharCounter>
        ) : null}
      </EditorFooter>
    </EditorWrapper>
  );
}

export { richTextToPlainText };
